import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import AdminFinanceForm from './AdminFinanceForm';
import FinanceIcon from './FinanceIcon';
import FinanceSubdivisions, { subdivisionTotal } from './FinanceSubdivisions';
import {
  createFinanceEntry,
  deleteFinanceEntry,
  getCurrentAdmin,
  getFinanceEntry,
  listFinances,
  listFinancesByPeriod,
  logoutAdmin,
  updateFinanceEntry,
  updateFinanceStatus,
} from '../services/api';
import './System.css';

const statusLabels = {
  pago: 'Pago/recebido',
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  cancelado: 'Cancelado',
};

const movementLabels = {
  entrada: 'Entrada',
  saida: 'Saída',
};

const recurrenceLabels = { avulso: 'Avulso', mensal: 'Mensal/recorrente' };

function localInputDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function createExtraField(label = '', value = '') {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    label,
    value,
  };
}

function currentMonth() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return `${year}-${month}`;
}

const emptyForm = {
  id: null,
  movementType: 'saida',
  recurrenceType: 'avulso',
  description: '',
  category: '',
  amount: '',
  status: 'pendente',
  transactionDate: localInputDate(),
  dueDate: '',
  notes: '',
  extraFields: [],
  subdivisions: [],
};

function monthLabel(month) {
  const [year, monthNumber] = month.split('-').map(Number);
  const date = new Date(year, monthNumber - 1, 1);

  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(date);
}

function dateLabel(date) {
  if (!date) {
    return '';
  }

  const [year, monthNumber, day] = date.split('-').map(Number);

  return new Intl.DateTimeFormat('pt-BR').format(new Date(year, monthNumber - 1, day));
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));
}

function currencyInputToNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  return (Number(digits) / 100).toFixed(2);
}

function formatCurrencyInput(value) {
  const normalizedValue = currencyInputToNumber(value);

  if (normalizedValue === '') {
    return '';
  }

  return formatCurrency(normalizedValue);
}

function parseExtraFields(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  try {
    const parsedValue = JSON.parse(value);

    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
}

function buildForm(entry) {
  return {
    id: entry.id,
    subdivisions: (entry.subdivisions || []).map((item) => ({ description: item.description, amount: item.amount })),
    movementType: entry.movement_type || 'saida',
    recurrenceType: entry.recurrence_active === undefined ? (entry.recurrence_type || 'avulso') : (Number(entry.recurrence_active) ? 'mensal' : 'avulso'),
    description: entry.description || '',
    category: entry.category || '',
    amount: entry.amount ? formatCurrency(entry.amount) : '',
    status: entry.status || 'pendente',
    transactionDate: entry.transaction_date || localInputDate(),
    dueDate: entry.due_date || '',
    notes: entry.notes || '',
    extraFields: parseExtraFields(entry.extra_fields).map((field) => createExtraField(field.label || '', field.value || '')),
  };
}

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function columnName(index) {
  let name = '';
  let currentIndex = index + 1;

  while (currentIndex > 0) {
    const remainder = (currentIndex - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    currentIndex = Math.floor((currentIndex - 1) / 26);
  }

  return name;
}

function getExtraColumns(entries) {
  const labels = [];

  entries.forEach((entry) => {
    parseExtraFields(entry.extra_fields).forEach((field) => {
      const label = String(field.label || '').trim();

      if (label && !labels.includes(label)) {
        labels.push(label);
      }
    });
  });

  return labels;
}

function cellXml(value, rowIndex, columnIndex, style = 0, type = 'text') {
  const reference = `${columnName(columnIndex)}${rowIndex}`;

  if (type === 'number') {
    return `<c r="${reference}" s="${style}"><v>${Number(value || 0).toFixed(2)}</v></c>`;
  }

  return `<c r="${reference}" t="inlineStr" s="${style}"><is><t>${escapeXml(value)}</t></is></c>`;
}

function rowXml(values, rowIndex, style = 0) {
  return `<row r="${rowIndex}">${values.map((value, index) => cellXml(value, rowIndex, index, style)).join('')}</row>`;
}

function rowCellsXml(cells, rowIndex) {
  return `<row r="${rowIndex}">${cells.join('')}</row>`;
}

function crc32(bytes) {
  const table = crc32.table || (crc32.table = Array.from({ length: 256 }, (_, index) => {
    let value = index;

    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }

    return value >>> 0;
  }));
  let crc = 0xffffffff;

  bytes.forEach((byte) => {
    crc = table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });

  return (crc ^ 0xffffffff) >>> 0;
}

function createZip(files) {
  const encoder = new TextEncoder();
  const chunks = [];
  const centralDirectory = [];
  let offset = 0;

  function writeUint16(view, position, value) {
    view.setUint16(position, value, true);
  }

  function writeUint32(view, position, value) {
    view.setUint32(position, value, true);
  }

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const contentBytes = encoder.encode(file.content);
    const checksum = crc32(contentBytes);
    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);

    writeUint32(localView, 0, 0x04034b50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0x0800);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, 0);
    writeUint16(localView, 12, 0);
    writeUint32(localView, 14, checksum);
    writeUint32(localView, 18, contentBytes.length);
    writeUint32(localView, 22, contentBytes.length);
    writeUint16(localView, 26, nameBytes.length);
    writeUint16(localView, 28, 0);
    localHeader.set(nameBytes, 30);
    chunks.push(localHeader, contentBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    writeUint32(centralView, 0, 0x02014b50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0x0800);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, 0);
    writeUint16(centralView, 14, 0);
    writeUint32(centralView, 16, checksum);
    writeUint32(centralView, 20, contentBytes.length);
    writeUint32(centralView, 24, contentBytes.length);
    writeUint16(centralView, 28, nameBytes.length);
    writeUint16(centralView, 30, 0);
    writeUint16(centralView, 32, 0);
    writeUint16(centralView, 34, 0);
    writeUint16(centralView, 36, 0);
    writeUint32(centralView, 38, 0);
    writeUint32(centralView, 42, offset);
    centralHeader.set(nameBytes, 46);
    centralDirectory.push(centralHeader);

    offset += localHeader.length + contentBytes.length;
  });

  const centralDirectoryOffset = offset;
  centralDirectory.forEach((header) => {
    chunks.push(header);
    offset += header.length;
  });

  const endHeader = new Uint8Array(22);
  const endView = new DataView(endHeader.buffer);
  writeUint32(endView, 0, 0x06054b50);
  writeUint16(endView, 8, files.length);
  writeUint16(endView, 10, files.length);
  writeUint32(endView, 12, offset - centralDirectoryOffset);
  writeUint32(endView, 16, centralDirectoryOffset);
  chunks.push(endHeader);

  return new Blob(chunks, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function createWorkbookXml(rows, maxColumnIndex, lastRowIndex) {
  const dimension = `A1:${columnName(maxColumnIndex)}${lastRowIndex}`;
  const titleMergeRange = `A1:${columnName(maxColumnIndex)}1`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheetPr><outlinePr summaryBelow="0"/></sheetPr>
  <dimension ref="${dimension}"/>
  <sheetViews><sheetView showGridLines="0" workbookViewId="0"><pane ySplit="10" topLeftCell="A11" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <cols>
    <col min="1" max="1" width="25" customWidth="1"/>
    <col min="2" max="2" width="68" customWidth="1"/>
    <col min="3" max="3" width="23" customWidth="1"/>
    <col min="4" max="4" width="25" customWidth="1"/>
    <col min="5" max="5" width="14" customWidth="1"/>
    <col min="6" max="6" width="22" customWidth="1"/>
    <col min="7" max="8" width="16" customWidth="1"/>
    <col min="9" max="9" width="20" customWidth="1"/>
    <col min="10" max="10" width="36" customWidth="1"/>
    <col min="11" max="${Math.max(12, maxColumnIndex + 1)}" width="24" customWidth="1"/>
  </cols>
  <sheetData>${rows.join('')}</sheetData>
  <mergeCells count="2"><mergeCell ref="${titleMergeRange}"/><mergeCell ref="A9:${columnName(maxColumnIndex)}9"/></mergeCells>
</worksheet>`;
}

function spreadsheetPeriodLabel(startMonth, endMonth) {
  if (!endMonth || startMonth === endMonth) {
    return monthLabel(startMonth);
  }

  return `${monthLabel(startMonth)} a ${monthLabel(endMonth)}`;
}

function spreadsheetDatePeriodLabel(startDate, endDate) {
  if (!endDate || startDate === endDate) {
    return dateLabel(startDate);
  }

  return `${dateLabel(startDate)} a ${dateLabel(endDate)}`;
}

function normalizeMonthRange(firstMonth, secondMonth) {
  const safeFirstMonth = firstMonth || currentMonth();
  const safeSecondMonth = secondMonth || safeFirstMonth;

  if (safeFirstMonth > safeSecondMonth) {
    return [safeSecondMonth, safeFirstMonth];
  }

  return [safeFirstMonth, safeSecondMonth];
}

function normalizeDateRange(firstDate, secondDate) {
  const safeFirstDate = firstDate || localInputDate();
  const safeSecondDate = secondDate || safeFirstDate;

  if (safeFirstDate > safeSecondDate) {
    return [safeSecondDate, safeFirstDate];
  }

  return [safeFirstDate, safeSecondDate];
}

export function downloadMonthlySpreadsheet(entries, summary, startPeriod, endPeriod = startPeriod, periodType = 'month') {
  const extraColumns = getExtraColumns(entries);
  const headers = ['Mês', 'Lançamento / subdivisão', 'Valor do detalhe', 'Total do lançamento', 'Tipo', 'Categoria', 'Data', 'Vencimento', 'Status', 'Observações', 'Frequência', 'Recorrência ativa', ...extraColumns];
  const periodLabel = periodType === 'date'
    ? spreadsheetDatePeriodLabel(startPeriod, endPeriod)
    : spreadsheetPeriodLabel(startPeriod, endPeriod);
  const rows = [
    rowXml([`Área Bliss - financeiro de ${periodLabel}`], 1, 1),
    rowXml(['Resumo do período', 'Valor'], 3, 2),
    rowCellsXml([cellXml('Total de entradas', 4, 0, 3), cellXml(summary.total_in, 4, 1, 4, 'number')], 4),
    rowCellsXml([cellXml('Total de saídas', 5, 0, 3), cellXml(summary.total_out, 5, 1, 4, 'number')], 5),
    rowCellsXml([cellXml('Saldo do período', 6, 0, 3), cellXml(summary.balance, 6, 1, 4, 'number')], 6),
    rowCellsXml([cellXml('Entradas pendentes', 7, 0, 3), cellXml(summary.pending_in, 7, 1, 4, 'number')], 7),
    rowCellsXml([cellXml('Saídas pendentes', 8, 0, 3), cellXml(summary.pending_out, 8, 1, 4, 'number')], 8),
    rowXml(['Os detalhes compõem o total do lançamento. Para somar o período, use somente a coluna Total do lançamento.'], 9),
    rowXml(headers, 10, 2),
  ];

  let nextRow = 11;
  const dateKey = (entry) => entry.transaction_date || (entry.transaction_date_label || '').split('/').reverse().join('-');
  const orderedEntries = [...entries].sort((a, b) => dateKey(a).localeCompare(dateKey(b)) || Number(a.id) - Number(b.id));
  orderedEntries.forEach((entry) => {
    const extraFields = parseExtraFields(entry.extra_fields);
    const extraValues = extraColumns.map((label) => {
      const field = extraFields.find((item) => item.label === label);

      return field?.value || '';
    });
    const rowIndex = nextRow++;
    const values = [
      monthLabel(dateKey(entry).slice(0, 7)), entry.description, '', '',
      movementLabels[entry.movement_type], entry.category || '',
      entry.transaction_date_label || dateLabel(entry.transaction_date), entry.due_date_label || '',
      statusLabels[entry.status], entry.notes || '',
      recurrenceLabels[entry.recurrence_type || 'avulso'],
      Number(entry.recurrence_active ?? (entry.recurrence_type === 'mensal')) ? 'Sim' : 'Não',
      ...extraValues,
    ];
    const height = Math.max(30, Math.ceil(String(entry.description || '').length / 60) * 16, Math.ceil(String(entry.notes || '').length / 32) * 16);
    rows.push(`<row r="${rowIndex}" ht="${height}" customHeight="1">${values.map((value, column) => column === 3 ? cellXml(entry.amount, rowIndex, column, 7, 'number') : cellXml(value, rowIndex, column, 5)).join('')}</row>`);
    (entry.subdivisions || []).forEach((item) => {
      const row = nextRow++;
      const detailHeight = Math.max(24, Math.ceil(String(item.description || '').length / 58) * 16);
      rows.push(`<row r="${row}" outlineLevel="1" ht="${detailHeight}" customHeight="1">${cellXml('↳ ' + item.description, row, 1, 6)}${cellXml(item.amount, row, 2, 4, 'number')}</row>`);
    });
  });
  const workbookFiles = [
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/><Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/><Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/></Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/><Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/></Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Financeiro mensal" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    {
      name: 'xl/worksheets/sheet1.xml',
      content: createWorkbookXml(rows, headers.length - 1, Math.max(10, nextRow - 1)),
    },
    {
      name: 'xl/styles.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="1"><numFmt numFmtId="164" formatCode="&quot;R$&quot; #,##0.00"/></numFmts><fonts count="4"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="16"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="4"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF1C1C1C"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF5E6CC"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left style="thin"><color rgb="FFD9D1C1"/></left><right style="thin"><color rgb="FFD9D1C1"/></right><top style="thin"><color rgb="FFD9D1C1"/></top><bottom style="thin"><color rgb="FFD9D1C1"/></bottom><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="8"><xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFill="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1"/><xf numFmtId="0" fontId="3" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyAlignment="1"><alignment indent="1" wrapText="1" vertical="center"/></xf><xf numFmtId="164" fontId="3" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`,
    },
    {
      name: 'docProps/core.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>Área Bliss - financeiro mensal</dc:title><dc:creator>Área Bliss</dc:creator><dcterms:created xsi:type="dcterms:W3CDTF">${new Date().toISOString()}</dcterms:created></cp:coreProperties>`,
    },
    {
      name: 'docProps/app.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"><Application>Área Bliss</Application></Properties>`,
    },
  ];

  const blob = createZip(workbookFiles);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `area-bliss-financeiro-${startPeriod}${endPeriod !== startPeriod ? `-a-${endPeriod}` : ''}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function AdminQuotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { entryId } = useParams();
  const isFormPage = location.pathname.endsWith('/novo') || Boolean(entryId);
  const listUrl = `/admin/financeiro${location.search}`;
  const [admin, setAdmin] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [viewMode, setViewMode] = useState('mensal');
  const [month, setMonth] = useState(currentMonth());
  const [startMonth, setStartMonth] = useState(currentMonth());
  const [endMonth, setEndMonth] = useState(currentMonth());
  const [refineByDays, setRefineByDays] = useState(false);
  const [startDate, setStartDate] = useState(localInputDate());
  const [endDate, setEndDate] = useState(localInputDate());
  const [filter, setFilter] = useState('todos');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [displayStartMonth, displayEndMonth] = normalizeMonthRange(startMonth, endMonth);
  const [displayStartDate, displayEndDate] = normalizeDateRange(startDate, endDate);

  useEffect(() => {
    let active = true;
    async function load() {
      setIsLoading(true);
      try {
        const me = await getCurrentAdmin();
        if (!active) return;
        setAdmin(me.admin);
      } catch (error) {
        if (active) navigate('/admin');
        return;
      }
      try {
        if (isFormPage) {
          const data = entryId ? await getFinanceEntry(entryId) : null;
          if (active) setForm(data ? buildForm(data.entry) : { ...emptyForm, transactionDate: localInputDate() });
        } else {
          const [safeStartMonth, safeEndMonth] = normalizeMonthRange(startMonth, endMonth);
          const [safeStartDate, safeEndDate] = normalizeDateRange(startDate, endDate);
          const financeData = viewMode === 'periodo'
            ? await listFinancesByPeriod(safeStartMonth, safeEndMonth, refineByDays ? { startDate: safeStartDate, endDate: safeEndDate } : {})
            : await listFinances(month);
          if (active) {
            setEntries(financeData.entries || []);
            setSummary(financeData.summary || {});
          }
        }
      } catch (error) {
        if (active) showMessage('error', error.message);
      } finally {
        if (active) setIsLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [endDate, endMonth, month, navigate, refineByDays, startDate, startMonth, viewMode, entryId, isFormPage]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (filter === 'todos') {
        return true;
      }

      if (filter === 'entrada' || filter === 'saida') {
        return entry.movement_type === filter;
      }

      if (filter === 'avulso' || filter === 'mensal') {
        return (entry.recurrence_type || 'avulso') === filter;
      }

      return entry.status === filter;
    });
  }, [entries, filter]);

  function showMessage(type, text) {
    setMessageType(type);
    setMessage(text);
  }

  function resetForm() {
    setForm({
      ...emptyForm,
      transactionDate: `${viewMode === 'periodo' ? startMonth : month}-01`,
    });
    setIsSaving(false);
  }

  function showList() {
    resetForm();
    navigate(listUrl);
    setMessage('');
  }

  function startCreating() {
    resetForm();
    navigate(`/admin/financeiro/novo${location.search}`);
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function updateField(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === 'amount' ? formatCurrencyInput(value) : value,
    }));
  }

  function addExtraField() {
    setForm((current) => ({
      ...current,
      extraFields: [...current.extraFields, createExtraField()],
    }));
  }

  function updateExtraField(index, key, value) {
    setForm((current) => ({
      ...current,
      extraFields: current.extraFields.map((field, fieldIndex) => (
        fieldIndex === index ? { ...field, [key]: value } : field
      )),
    }));
  }

  function removeExtraField(index) {
    setForm((current) => ({
      ...current,
      extraFields: current.extraFields.filter((_, fieldIndex) => fieldIndex !== index),
    }));
  }

  async function refreshFinances() {
    const [safeStartMonth, safeEndMonth] = normalizeMonthRange(startMonth, endMonth);
    const [safeStartDate, safeEndDate] = normalizeDateRange(startDate, endDate);
    const financeData = viewMode === 'periodo'
      ? await listFinancesByPeriod(safeStartMonth, safeEndMonth, refineByDays ? { startDate: safeStartDate, endDate: safeEndDate } : {})
      : await listFinances(month);
    setEntries(financeData.entries || []);
    setSummary(financeData.summary || {});
  }

  async function handleRefresh() {
    setIsRefreshing(true);
    setMessage('');

    try {
      await refreshFinances();
      showMessage('success', 'Lançamentos atualizados.');
    } catch (error) {
      showMessage('error', error.message);
    } finally {
      setIsRefreshing(false);
    }
  }

  async function handleSpreadsheetDownload() {
    setIsExporting(true);
    setMessage('');

    try {
      const reportStartMonth = viewMode === 'periodo' ? displayStartMonth : month;
      const reportEndMonth = viewMode === 'periodo' ? displayEndMonth : month;
      const reportStartPeriod = viewMode === 'periodo' && refineByDays ? displayStartDate : reportStartMonth;
      const reportEndPeriod = viewMode === 'periodo' && refineByDays ? displayEndDate : reportEndMonth;

      downloadMonthlySpreadsheet(
        entries,
        summary,
        reportStartPeriod,
        reportEndPeriod,
        viewMode === 'periodo' && refineByDays ? 'date' : 'month',
      );
    } catch (error) {
      showMessage('error', error.message);
    } finally {
      setIsExporting(false);
    }
  }

  function updateViewMode(mode) {
    setViewMode(mode);
    setMessage('');

    if (mode === 'periodo') {
      setStartMonth((current) => current || month);
      setEndMonth((current) => current || month);
      setStartDate((current) => current || `${month}-01`);
      setEndDate((current) => current || localInputDate());
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setMessage('');

    try {
      if (form.subdivisions.some((item) => !item.description.trim() || !Number.isFinite(Number(item.amount)) || Number(item.amount) <= 0)) {
        throw new Error('Informe descrição e valor maior que zero em todas as subdivisões.');
      }
      const payload = { ...form, amount: form.subdivisions.length ? subdivisionTotal(form.subdivisions).toFixed(2) : currencyInputToNumber(form.amount) };
      if (form.id) {
        await updateFinanceEntry(payload);
        showMessage('success', 'Lançamento atualizado.');
      } else {
        await createFinanceEntry(payload);
        showMessage('success', 'Lançamento cadastrado.');
      }

      resetForm();
      navigate(listUrl);
    } catch (error) {
      showMessage('error', error.message);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleStatusChange(id, status) {
    setMessage('');

    try {
      await updateFinanceStatus(id, status);
      await refreshFinances();
      showMessage('success', 'Status atualizado.');
    } catch (error) {
      showMessage('error', error.message);
    }
  }

  function startEditing(entry) {
    navigate(`/admin/financeiro/${entry.id}/editar${location.search}`);
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleDelete(entry) {
    const confirmed = window.confirm(`Remover "${entry.description}" do financeiro desta visualização? O registro ficará preservado no banco com data de exclusão.`);

    if (!confirmed) {
      return;
    }

    setMessage('');

    try {
      await deleteFinanceEntry(entry.id);
      await refreshFinances();
      if (form.id === entry.id) {
        resetForm();
      }
      showMessage('success', 'Lançamento removido.');
    } catch (error) {
      showMessage('error', error.message);
    }
  }

  async function handleLogout() {
    await logoutAdmin();
    navigate('/admin');
  }

  if (isLoading) {
    return (
      <main className="system-page">
        <section className="system-shell">
          <p>Carregando financeiro mensal...</p>
        </section>
      </main>
    );
  }

  return (
    <main className="system-page admin-page" translate="no">
      <section className="system-shell admin-shell">
        <header className="admin-toolbar">
          <div>
            <span className="eyebrow">Área Bliss</span>
            <h1>{isFormPage ? (entryId ? 'Editar lançamento' : 'Novo lançamento') : 'Financeiro'}</h1>
            <p>{admin?.name ? `Logado como ${admin.name}` : 'Entradas, saídas e saldo da empresa'}</p>
          </div>

          <div className="toolbar-actions">
            <Link to="/" className="secondary-action icon-only" aria-label="Ir para o site" title="Ir para o site">
              <FinanceIcon name="home" />
            </Link>
            <button type="button" className="icon-only" onClick={handleLogout} aria-label="Sair" title="Sair">
              <FinanceIcon name="logout" />
            </button>
          </div>
        </header>

        {!isFormPage && (<>
        <div className="month-toolbar">
          <div className="view-controls">
            <div className="filters view-mode" aria-label="Modo de visualização">
              <button
                type="button"
                className={viewMode === 'mensal' ? 'active' : ''}
                aria-pressed={viewMode === 'mensal'}
                onClick={() => updateViewMode('mensal')}
              >
                <span>Mensal</span>
              </button>
              <button
                type="button"
                className={viewMode === 'periodo' ? 'active' : ''}
                aria-pressed={viewMode === 'periodo'}
                onClick={() => updateViewMode('periodo')}
              >
                <span>Período</span>
              </button>
            </div>

            <label className="date-filter">
              {viewMode === 'periodo' ? 'Período de' : 'Mês exibido'}
              <input
                type="month"
                value={viewMode === 'periodo' ? startMonth : month}
                onChange={(event) => {
                  const value = event.target.value || currentMonth();

                  if (viewMode === 'periodo') {
                    setStartMonth(value);
                    return;
                  }

                  setMonth(value);
                }}
              />
            </label>

            {viewMode === 'periodo' && (
              <label className="date-filter">
                Até
                <input type="month" value={endMonth} onChange={(event) => setEndMonth(event.target.value || startMonth)} />
              </label>
            )}

            {viewMode === 'periodo' && (
              <label className="toggle-filter">
                <input
                  type="checkbox"
                  checked={refineByDays}
                  onChange={(event) => setRefineByDays(event.target.checked)}
                />
                <span>Refinar por dias</span>
              </label>
            )}

            {viewMode === 'periodo' && refineByDays && (
              <>
                <label className="date-filter">
                  Data inicial
                  <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value || localInputDate())} />
                </label>

                <label className="date-filter">
                  Data final
                  <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value || startDate)} />
                </label>
              </>
            )}
          </div>

          <div className="report-actions">
          <button type="button" className="secondary-button icon-only" onClick={handleRefresh} disabled={isRefreshing} aria-label={isRefreshing ? 'Atualizando lançamentos' : 'Atualizar lançamentos'} title="Atualizar lançamentos">
            <FinanceIcon name="refresh" />
          </button>

          <button type="button" className="secondary-button icon-only" onClick={handleSpreadsheetDownload} disabled={isExporting} aria-label="Exportar Excel" title="Exportar Excel">
            <FinanceIcon name="download" />
          </button>
          </div>
        </div>

        <section
          className="summary-grid"
          aria-label={`Resumo financeiro de ${viewMode === 'periodo' ? (refineByDays ? spreadsheetDatePeriodLabel(displayStartDate, displayEndDate) : spreadsheetPeriodLabel(displayStartMonth, displayEndMonth)) : monthLabel(month)}`}
        >
          <article className="summary-card income">
            <span>{viewMode === 'periodo' ? 'Entrou no período' : 'Entrou no mês'}</span>
            <strong>{formatCurrency(summary.total_in)}</strong>
          </article>
          <article className="summary-card outcome">
            <span>{viewMode === 'periodo' ? 'Saiu no período' : 'Saiu no mês'}</span>
            <strong>{formatCurrency(summary.total_out)}</strong>
          </article>
          <article className="summary-card balance">
            <span>{viewMode === 'periodo' ? 'Saldo do período' : 'Saldo do mês'}</span>
            <strong>{formatCurrency(summary.balance)}</strong>
          </article>
          <article className="summary-card pending">
            <span>{viewMode === 'periodo' ? 'Pendente no período' : 'Pendente até o fim do mês'}</span>
            <strong>{formatCurrency(Number(summary.pending_in || 0) + Number(summary.pending_out || 0))}</strong>
          </article>
        </section>

        <div className="list-heading">
          <h2>Lançamentos</h2>
          <button type="button" className="primary-button icon-action" onClick={startCreating}>
            <FinanceIcon name="plus" /><span>Novo</span>
          </button>
        </div>
        </>)}

        {message && <p role={messageType === 'error' ? 'alert' : 'status'} className={`form-alert ${messageType}`}>{message}</p>}

        {isFormPage && !isLoading && (!entryId || String(form.id) === entryId) && (
          <AdminFinanceForm form={form} statusLabels={statusLabels} isSaving={isSaving}
            onSubdivisionsChange={(subdivisions) => setForm((current) => ({ ...current, subdivisions }))}
            onSubmit={handleSubmit} onCancel={showList} updateField={updateField}
            addExtraField={addExtraField} updateExtraField={updateExtraField} removeExtraField={removeExtraField} />
        )}
        {isFormPage && entryId && !form.id && <Link to={listUrl}>Voltar para lançamentos</Link>}

        {!isFormPage && (
        <>
        <div className="admin-filters">
          <label className="date-filter entry-filter">
            Filtrar lançamentos
            <select value={filter} onChange={(event) => setFilter(event.target.value)}>
              {[
                ['todos', 'Todos'], ['entrada', 'Entradas'], ['saida', 'Saídas'],
                ['avulso', 'Avulsos'], ['mensal', 'Mensais/recorrentes'],
                ...Object.entries(statusLabels),
              ].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <span className="helper-text">{filteredEntries.length} lançamento(s)</span>
        </div>

        <div className="quotes-list">
          {filteredEntries.length === 0 && <p>Nenhum lançamento encontrado para este filtro.</p>}

          {filteredEntries.map((entry) => (
            <article className={`quote-card finance-card ${entry.movement_type}`} key={entry.id}>
              <div className="quote-card-header">
                <div>
                  <span className="movement-badge">{movementLabels[entry.movement_type]}</span>
                  <span className="movement-badge">{recurrenceLabels[entry.recurrence_type || 'avulso']}</span>
                  {entry.recurrence_type === 'mensal' && entry.recurrence_active !== undefined && !Number(entry.recurrence_active) && <span className="helper-text">Recorrência interrompida</span>}
                  <h2>{entry.description}</h2>
                  <p>{entry.category || 'Sem categoria'}</p>
                </div>

                <div className="finance-card-control">
                  <strong>{formatCurrency(entry.amount)}</strong>
                  <select
                    value={entry.status}
                    onChange={(event) => handleStatusChange(entry.id, event.target.value)}
                    aria-label={`Status de ${entry.description}`}
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <dl className="quote-details finance-details">
                <div>
                  <dt>Data</dt>
                  <dd>{entry.transaction_date_label}</dd>
                </div>
                <div>
                  <dt>Vencimento</dt>
                  <dd>{entry.due_date_label || 'Não informado'}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{statusLabels[entry.status]}</dd>
                </div>
              </dl>

              {entry.subdivisions?.length > 0 && <FinanceSubdivisions items={entry.subdivisions} formatCurrency={formatCurrency} />}
              {entry.notes && <p className="quote-notes">{entry.notes}</p>}

              {parseExtraFields(entry.extra_fields).length > 0 && (
                <dl className="quote-details extra-details">
                  {parseExtraFields(entry.extra_fields).map((field) => (
                    <div key={`${entry.id}-${field.label}`}>
                      <dt>{field.label}</dt>
                      <dd>{field.value || 'Não informado'}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <div className="quote-footer">
                <p className="quote-created">Registrado em {entry.created_at}</p>
                <div className="quote-actions">
                  <button type="button" className="secondary-button icon-only" onClick={() => startEditing(entry)} aria-label={`Editar ${entry.description}`} title="Editar lançamento">
                    <FinanceIcon name="edit" />
                  </button>
                  <button type="button" className="danger-button icon-only" onClick={() => handleDelete(entry)} aria-label={`Remover ${entry.description}`} title="Remover lançamento">
                    <FinanceIcon name="trash" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
        </>
        )}
      </section>
    </main>
  );
}

export default AdminQuotes;
